import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { getDetails } from "../services/truckAssets";
import { useSource } from "./useSource";

const PhotosContext = createContext();

function PhotosProvider({ children }) {
  // various states
  // TODO: Move these into reducers?
  const [truckDetails, setTruckDetails] = useState();
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const { source } = useSource();

  // reload data when new search text is input
  useEffect(() => {
    (async () => {
      if (searchText && source) {
        await fetchTruckDetails(searchText, false);
      }
    })();
  }, [searchText, source]);

  // basic details/truck info stuff
  const fetchTruckDetails = useCallback(
    async (searchText, hideLoader = false) => {
      if (!hideLoader) {
        setLoading(true);
      }

      const truckDetails = await getDetails(searchText, source);

      setTruckDetails(truckDetails);

      updateSelectedItems(truckDetails);

      setLoading(false);

      return "success";
    },
    [setLoading, getDetails, setTruckDetails, setLoading, source]
  );

  // item selection things
  const setSelectedItem = useCallback(
    (assetCheck) => {
      if (isItemSelected(assetCheck)) {
        return;
      }

      setSelectedItems([...selectedItems, assetCheck]);
    },
    [selectedItems, setSelectedItems]
  );

  // This is to prevent replaced items from still being selected and to
  // update assets when their details have been edited.
  const updateSelectedItems = useCallback(
    (truckDetails) => {
      setSelectedItems((previousState) => {
        const stateCopy = [...previousState];

        const updatedState = stateCopy
          .map((asset) => {
            const matchedAsset = truckDetails.assets.find(
              (updatedAsset) => updatedAsset.id === asset.id
            );

            return matchedAsset ? matchedAsset : null;
          })
          .filter((asset) => asset);

        return updatedState;
      });
    },
    [setSelectedItems]
  );

  const unsetSelectedItem = useCallback(
    (assetCheck) => {
      const assetIdIndex = selectedItems.findIndex(
        (asset) => asset.id === assetCheck.id
      );

      let newArray = [...selectedItems];

      if (assetIdIndex >= 0) {
        newArray.splice(assetIdIndex, 1);
      }

      setSelectedItems(newArray);
    },
    [selectedItems, setSelectedItems]
  );

  const isItemSelected = useCallback(
    (assetCheck) => {
      return (
        selectedItems.find((asset) => asset.id === assetCheck.id) !== undefined
      );
    },
    [selectedItems]
  );

  const clearSelectedItems = useCallback(() => {
    setSelectedItems([]);
  }, [setSelectedItems]);

  // items that get shared from the hook
  const value = {
    loading,
    searchText,
    truckDetails,
    selectedItems,
    setSelectedItem,
    unsetSelectedItem,
    isItemSelected,
    clearSelectedItems,
    getTruckDetails: useCallback(
      async (searchText) => {
        setSearchText(searchText);
      },
      [setSearchText, source]
    ),
    refetch: useCallback(
      async (stockNumber, hideLoader = true) => {
        await fetchTruckDetails(stockNumber, hideLoader);
      },
      [fetchTruckDetails]
    ),
  };

  // create the context with the required values
  return (
    <PhotosContext.Provider value={value}>{children}</PhotosContext.Provider>
  );
}

function usePhotos() {
  const context = useContext(PhotosContext);

  if (context === undefined) {
    throw new Error("usePhotos must be used within a PhotosProvider");
  }

  return context;
}

export { PhotosProvider, usePhotos };
